<template>
    <c-modal :value="true" size="xl" title="Link Layers to Auto-Fill Values" @closed="closeModal">
        <p class="subtitle">
            Link layers to auto-fill their values, with inputs from other elements.
        </p>
        <div class="options-card">
            <div class="form-group w-100">
                <label>Autofill Layer</label>
                <div class="fake-input disabled-white">
                    <div class="source-option">
                        <span class="source-badge">
                            <svg-icon
                                v-if="targetIcon"
                                :src="targetIcon"
                                class="source-icon"
                            />
                            <span class="source-id">{{ targetElement.id }}</span>
                        </span>
                        <span class="source-name">{{ targetElement.name }}</span>
                    </div>
                </div>
            </div>
            <div class="form-group w-100 d-flex gap-3">
                <div class="select-column">
                    <label>With Input from Layer</label>
                    <c-select
                        v-model="selectedSource"
                        :options="sources"
                        placeholder="Select source"
                        :append-to-body="true"
                        class="w-100"
                    >
                        <template #option="option">
                            <div class="source-option">
                                <span class="source-badge">
                                    <svg-icon
                                        v-if="getSourceIcon(option.value)"
                                        :src="getSourceIcon(option.value)"
                                        class="source-icon"
                                    />
                                    <span class="source-id">{{ getSourceId(option.value) }}</span>
                                </span>
                                <span class="source-name">{{ option.label }}</span>
                            </div>
                        </template>
                        <template #selected-option="option">
                            <div class="source-option">
                                <span class="source-badge">
                                    <svg-icon
                                        v-if="getSourceIcon(option.value)"
                                        :src="getSourceIcon(option.value)"
                                        class="source-icon"
                                    />
                                    <span class="source-id">{{ getSourceId(option.value) }}</span>
                                </span>
                                <span class="source-name">{{ option.label }}</span>
                            </div>
                        </template>
                    </c-select>
                </div>
                <div class="select-column">
                    <label>With Value Format</label>
                    <c-select
                        v-model="selectedValueFormat"
                        :options="valueFormats"
                        placeholder="Select value format"
                        :append-to-body="true"
                        class="w-100"
                    />
                </div>
            </div>
        </div>
        <template #modal-footer>
            <div class="d-flex justify-content-between w-100">
                <c-button text="Cancel" variant="secondary" @click="closeModal" />
                <div class="d-flex">
                    <c-button
                        v-if="targetElement.sourceElement != null"
                        text="Unlink"
                        variant="danger"
                        class="me-2"
                        @click="onUnlinkHandler"
                    />
                    <c-button
                        text="Apply Link"
                        variant="primary"
                        :disabled="!canApplyLink"
                        @click="onConfirmHandler"
                    />
                </div>
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, Ref } from 'vue';
import { BoundedTextElement, CanvasElement } from '@/typings/customilyElements/canvasElements';
import { ValueFormat, SourceValueFormatOptions, MAP_VALUE_FORMATS, ElementType } from '@/typings/designStudio/sourceElementTypes';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CSelect from '@/components/shared/CSelect/CSelect.vue';
import { CSelectItem } from '../shared/CSelect';
import { getElementType } from '@/utils/elementTypeUtils';
import SvgIcon from '@/components/SvgIcon/SvgIcon.vue';
import { getCanvasElementIcon } from '@/utils/elementsUtils';

const props = defineProps<{
    targetElement: BoundedTextElement;
    sourceElements: CanvasElement[];
}>();

const emit = defineEmits<{
    (event: 'applyLink', payload: { sourceElement: CanvasElement; sourceType: ElementType; valueFormat: ValueFormat }): void;
    (event: 'close'): void;
    (event: 'unlink'): void;
}>();

const selectedSource: Ref<CSelectItem<string> | undefined> = ref();
const selectedValueFormat: Ref<CSelectItem<ValueFormat> | undefined> = ref();
    
const sources = computed(() =>
    props.sourceElements
        .filter(e => typeof e.uuid === 'string' && e.uuid.length > 0)
        .map(e => ({
            label: e.name ?? '',
            value: e.uuid as string
        }))
);

const valueFormats = computed(() => {
    const source = props.sourceElements.find(e => e.uuid === selectedSource.value?.value);
    if (!source) {
        return [];
    }
    const sourceType = getElementType(source);
    if (sourceType === ElementType.Map) {
        return SourceValueFormatOptions.filter(opt =>
            MAP_VALUE_FORMATS.includes(opt.value)
        );
    }
    return [];
});

watch(
    () => props.targetElement.sourceElement,
    (newVal) => {
        if (newVal) {
            const sourceMatch = sources.value.find(opt => opt.value === newVal.sourceId);
            selectedSource.value = sourceMatch ? sourceMatch : undefined;

            const valueFormatMatch = valueFormats.value.find(opt => opt.value === newVal.valueFormat);
            selectedValueFormat.value = valueFormatMatch ? valueFormatMatch : undefined;
        }
    },
    { immediate: true }
);

watch(selectedSource, (newVal) => {
    if (!newVal) {
        selectedValueFormat.value = undefined;
    }
});

watch(
  () => sources.value,
  (newSources) => {
    if (!newSources || newSources.length === 0) {
      selectedSource.value = undefined;
      return;
    }
    const found = newSources.find(opt => opt.value === selectedSource.value?.value);
    if (!found) {     
      selectedSource.value = newSources[0];
    }
  },
  { immediate: true }
);

watch(
  () => valueFormats.value,
  (newValueFormats) => {
    if (!newValueFormats || newValueFormats.length === 0) {
      selectedValueFormat.value = undefined;
      return;
    }
    const found = newValueFormats.find(opt => opt.value === selectedValueFormat.value?.value);
    if (!found) {     
      selectedValueFormat.value = newValueFormats[0];
    }
  },
  { immediate: true }
);

const targetIcon = computed(() => getCanvasElementIcon(props.targetElement) || '');

const getSourceIcon = (uuid: string | undefined) => {
    if (!uuid) return '';
    const element = props.sourceElements.find(e => e.uuid === uuid);
    if (!element) return '';
    return getCanvasElementIcon(element) || '';
};

const getSourceId = (uuid: string | undefined) => {
    if (!uuid) return '';
    const element = props.sourceElements.find(e => e.uuid === uuid);
    return element?.id ?? '';
};

const closeModal = () => emit('close');
const onConfirmHandler = () => {
    if (!selectedSource.value || !selectedValueFormat.value) {
        return;
    }
    const sourceElement = props.sourceElements.find(e => e.uuid === selectedSource.value?.value);
    if(!sourceElement) {
        return;
    }
    const sourceType = getElementType(sourceElement);
    if(!sourceType) {
        return;
    }
    emit('applyLink', {
        sourceElement: sourceElement,
        valueFormat: selectedValueFormat.value?.value,
        sourceType: sourceType
    });
};

const onUnlinkHandler = () => {
    emit('unlink');
};

const canApplyLink = computed(() =>
    !!selectedSource.value && !!selectedValueFormat.value
);
</script>

<style scoped lang="scss">
.subtitle {
    color: #000;
    font-family: Arial, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin-bottom: 16px;
}

.options-card {
    display: flex;
    padding: 16px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    align-self: stretch;
    border-radius: 4px;
    border: 1px solid #e6e6e6;
    background: #f7f7f7;
}

.form-group {
    margin-bottom: 0;
}

.w-100 {
    width: 100%;
}

.gap-3 {
    gap: 1rem;
}

.select-column {
    flex: 1 1 0;
    min-width: 0;
}
.disabled-white :deep(input),
.disabled-white :deep(.form-control),
.disabled-white :deep(.form-control:disabled) {
    background-color: #fff !important;
    cursor: default;
}

.source-option {
    display: flex;
    align-items: center;
    gap: 8px;
}

.source-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: #e9e9e9;
    border-radius: 4px;
    padding: 2px 8px;
    min-width: fit-content;
}

.source-icon {
    font-size: 14px;
    color: #333;
    fill: #333;
}

.source-id {
    font-family: Arial;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    color: #333;
}

.source-name {
    font-family: Arial;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: #333;
}
.fake-input {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 38px;
    padding: 4px 10px;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    background-color: #fff;
    cursor: default;
}
</style>